import type { ReactNode } from "react";
import {
  useThumbnail,
  type ThumbnailKind,
} from "@/features/coach-review/hooks/useThumbnail";

export type FeedbackMediaThumbnailProps = {
  kind: ThumbnailKind;
  id: number | null;
  className: string;
  testId: string;
  placeholderTestId: string;
  imgClassName?: string;
  children?: ReactNode;
  /** Whether to actually fetch the thumbnail (#210). Default true so
   * existing callers (clip composer, focused player) are unchanged.
   * The Feedback Library passes `note.has_thumbnail` here so a long
   * rail of thumbnail-less notes doesn't fan out 404 GETs on mount. */
  enabled?: boolean;
};

/** Shared auth-fetched note/clip thumbnail tile.
 *
 * Note and clip thumbnails are private JPEG blobs served through the
 * coach thumbnail endpoints, so every surface must use `useThumbnail`
 * rather than direct `<img src="/api/...">` URLs. Children render as
 * overlays, used by video-note rows that need saved telestration marks
 * on top of the server frame.
 */
export function FeedbackMediaThumbnail({
  kind,
  id,
  className,
  testId,
  placeholderTestId,
  imgClassName = "block h-full w-full object-cover",
  children,
  enabled = true,
}: FeedbackMediaThumbnailProps) {
  const url = useThumbnail(kind, id, enabled);
  return (
    <span
      aria-hidden="true"
      className={className}
      data-testid={url ? testId : placeholderTestId}
    >
      {url ? <img src={url} alt="" className={imgClassName} /> : null}
      {children}
    </span>
  );
}
